import {
  Button,
  CellText,
  DataTable,
  EmptyState,
  KeyValue,
  List,
  ListRow,
  Icon,
  PageHeader,
  Section,
  Skeleton,
  Text,
} from '@ui';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import { ExportButton } from '../../reports/components/ExportButton';
import { useStock, type StockOverview } from '../api';
import { useRowMenu } from '../../../app/rowActions';
import { useBatchActions } from '../components/BatchActions';
import { FillingDialog } from '../components/FillingDialog';
import { formatKg } from '../format';
import styles from './HarvestPages.module.css';

type ArticleRow = StockOverview['articles'][number];

/** Stock overview: bulk per variety, jars per article, kg equivalent (INV-023). */
export function StockPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const stock = useStock();
  const [filling, setFilling] = useState(false);
  const rowMenu = useRowMenu();
  const batchActions = useBatchActions();
  const data = stock.data;
  const linkBatch = can('harvest.read');

  /** The lots of a row, linked to their batch for those who may see it. */
  const lots = (items: { id: string; text: string }[]) =>
    items.map((l, i) => (
      <Fragment key={l.id}>
        {i > 0 && ' · '}
        {linkBatch ? <Link to={`/ernte/${l.id}`}>{l.text}</Link> : l.text}
      </Fragment>
    ));

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('stock.title')}
        actions={
          <>
            {can('stock.write') && (
              <Button onClick={() => setFilling(true)} iconStart={<Icon name="plus" size="sm" />}>
                {t('harvest.filling.action')}
              </Button>
            )}
            <ExportButton list="stock" />
          </>
        }
      />
      {stock.error && (
        <Text tone="danger" role="alert">
          {errorMessage(stock.error)}
        </Text>
      )}
      {stock.isPending && <Skeleton lines={6} />}
      {data &&
        (data.articles.length === 0 && data.bulk.length === 0 ? (
          <EmptyState
            icon="package"
            title={t('stock.emptyTitle')}
            {...(can('harvest.write')
              ? { actions: <Link to="/ernte">{t('stock.toHarvest')}</Link> }
              : {})}
          />
        ) : (
          <div className={styles.columns}>
            <div className={styles.main}>
              <Section title={t('stock.articles')} variant="flush">
                <DataTable
                  caption={t('stock.articles')}
                  rows={data.articles}
                  rowKey={(a) => a.article.id}
                  rowActions={rowMenu.table<ArticleRow>(
                    (a) => a.article.name,
                    // A correction belongs to one batch: offered when the jars come from one.
                    (a) =>
                      a.batches.length === 1 && a.batches[0]
                        ? batchActions.items(a.batches[0].batch_id, { edit: false, bulkGrams: 0 })
                        : [],
                  )}
                  columns={[
                    {
                      key: 'article',
                      header: t('stock.columns.article'),
                      primary: true,
                      cell: (a: ArticleRow) => (
                        <CellText
                          main={a.article.name}
                          sub={lots(
                            a.batches.map((b) => ({
                              id: b.batch_id,
                              text: t('stock.lotCount', { lot: b.lot, count: b.jars }),
                            })),
                          )}
                        />
                      ),
                    },
                    {
                      key: 'jars',
                      header: t('stock.columns.jars'),
                      align: 'end',
                      cell: (a) => (
                        <span className={a.jars < 0 ? styles.negative : undefined}>{a.jars}</span>
                      ),
                    },
                    {
                      key: 'kg',
                      header: t('stock.columns.kg'),
                      align: 'end',
                      mobile: 'hidden',
                      cell: (a) => formatKg(a.jars * a.net_weight_grams),
                    },
                  ]}
                  empty={
                    <Text tone="muted" className={styles.tableEmpty}>
                      {t('stock.noJars')}
                    </Text>
                  }
                />
              </Section>
            </div>
            <div className={styles.side}>
              <Section title={t('stock.total')}>
                <KeyValue
                  items={[
                    {
                      key: 'total',
                      label: t('stock.totalHoney'),
                      value: formatKg(data.total_grams, 1),
                    },
                    {
                      key: 'bulk',
                      label: t('stock.bulk'),
                      value: formatKg(data.total_bulk_grams, 1),
                    },
                    {
                      key: 'jars',
                      label: t('stock.jars'),
                      value: t('harvest.articles.count', { count: data.total_jars }),
                    },
                  ]}
                />
              </Section>
              <Section title={t('stock.bulkByVariety')}>
                {data.bulk.length === 0 ? (
                  <Text tone="muted" className={styles.empty}>
                    {t('stock.noBulk')}
                  </Text>
                ) : (
                  <List>
                    {data.bulk.map((v) => (
                      <ListRow
                        key={v.variety.id}
                        title={v.variety.name}
                        description={lots(
                          v.batches.map((b) => ({
                            id: b.batch_id,
                            text: `${b.lot}: ${formatKg(b.bulk_grams)}`,
                          })),
                        )}
                        trailing={formatKg(v.bulk_grams)}
                        actionsLabel={rowMenu.label(v.variety.name)}
                        actions={
                          v.batches.length === 1 && v.batches[0]
                            ? batchActions.items(v.batches[0].batch_id, { edit: false })
                            : can('stock.write')
                              ? [
                                  {
                                    key: 'fill',
                                    label: t('harvest.filling.action'),
                                    onSelect: () => setFilling(true),
                                  },
                                ]
                              : []
                        }
                      />
                    ))}
                  </List>
                )}
              </Section>
            </div>
          </div>
        ))}
      {filling && <FillingDialog open onClose={() => setFilling(false)} />}
      {batchActions.dialogs}
    </div>
  );
}
